// Step 10 commit 1 (DIRECTIVE-035): locked packing matrix reference.
// Source of truth: PROJECT_MEMORY section 5 "Packing & Pricing Logic"
// (packing matrix locked Jun 10, 2026). ANY discrepancy between portal
// data and this module means the portal data is wrong - fix the data,
// never this module, unless Alexis amends the lock.

import type {
  BoxCapacity,
  BoxSizes,
  SaleMode,
  UnitsPerBox,
  Variety,
} from "@/lib/catalog/types";
import { subCategoryOf, type SubCategory } from "@/lib/catalog/compatibility";

// Category-level matrix rows (PROJECT_MEMORY section 5).
export const ROSE_50_60: BoxCapacity = { EB: null, QB: 125, HB: 300 };
export const ROSE_70: BoxCapacity = { EB: null, QB: 100, HB: 300 };
export const GARDEN_ROSE: BoxCapacity = { EB: null, QB: 100, HB: 300 };
export const SPRAY_ROSE: BoxCapacity = { EB: null, QB: 120, HB: 240 };
// Hydrangea Select grade. Premium (35) and Mini (60) are per-variety below.
export const HYDRANGEA: BoxCapacity = { EB: 20, QB: 40, HB: null };
export const SPECIALTY_DEFAULT: BoxCapacity = { EB: 80, QB: 140, HB: null };
export const CARNATION: BoxCapacity = { EB: 100, QB: 200, HB: 400 };

// RETIRED (Alexis ruling, Jul 13, 2026). The old filler default was
// { EB: 100, QB: 300 }, and NOT ONE of the 23 filler/green varieties still
// matches it - every one was re-measured in packing-audit.xlsx. Fillers are
// now a per-variety table (FILLER_OVERRIDES below), not a category default.
// Kept only so an accidental import fails loudly rather than silently
// resurrecting the dead numbers.
export const FILLER_GREEN_RETIRED: BoxCapacity = {
  EB: null,
  QB: null,
  HB: null,
};

// Per-variety filler/green capacities, re-measured by Alexis and locked from
// packing-audit.xlsx (Jul 13, 2026). This REPLACES the old category default.
// Every filler variety must appear here.
export const FILLER_OVERRIDES: Record<string, BoxCapacity> = {
  "achillea-yellow": { EB: 80, QB: 150, HB: null },
  alstroemeria: { EB: 100, QB: 180, HB: null },
  "babys-breath": { EB: 80, QB: 150, HB: null },
  brillantina: { EB: 60, QB: 100, HB: null },
  bupleurum: { EB: 80, QB: 130, HB: null },
  craspedia: { EB: 150, QB: 300, HB: null },
  "dusty-miller": { EB: 80, QB: 140, HB: null },
  "eucalyptus-baby-blue": { EB: 80, QB: 150, HB: null },
  "eucalyptus-parvifolia": { EB: 100, QB: 200, HB: null },
  "eucalyptus-silver-dollar": { EB: 80, QB: 120, HB: null },
  "green-ball": { EB: 80, QB: 120, HB: null },
  hypericum: { EB: 100, QB: 200, HB: null },
  // Alexis ruling Jul 13, 2026: QB 400 -> 150. This SUPERSEDES the old locked
  // note "400 stems/box, lower freight" from Jun 12.
  "leather-fern": { EB: 100, QB: 150, HB: null },
  lepidium: { EB: 80, QB: 130, HB: null },
  // Largest non-HB capacity in the catalog - confirmed by Alexis Jul 13, 2026.
  "lily-grass": { EB: 250, QB: 500, HB: null },
  limonium: { EB: 80, QB: 130, HB: null },
  "rumex-green": { EB: 80, QB: 150, HB: null },
  ruscus: { EB: 100, QB: 200, HB: null },
  scabiosa: { EB: 80, QB: 120, HB: null },
  smilax: { EB: 100, QB: 200, HB: null },
  solidago: { EB: 80, QB: 130, HB: null },
  statice: { EB: 80, QB: 150, HB: null },
  strawflower: { EB: 80, QB: 120, HB: null },
};

export const STEMS_PER_BUNCH: Record<string, number | null> = {
  rose: 25,
  "garden-rose": 25,
  "spray-rose": 10,
  hydrangea: null, // sold by box, single stems
  specialty: 10,
  "select-carnation": 25,
  "spray-carnation": 10,
  "filler-green": 10,
};

// Per-variety overrides CONFIRMED by Alexis at Step 10 Pause Point 1
// (Jun 12, 2026). These deviate from the category defaults for physical
// packing reasons (bulky/dense stems) and are locked in PROJECT_MEMORY
// section 5 alongside the category matrix. Any change requires a new
// Alexis ruling; edit here and the packing-matrix test will fail on
// every file that still carries the old numbers.
export const PER_VARIETY_OVERRIDES: Record<string, BoxCapacity> = {
  // Hydrangea grades (Alexis, Jul 13, 2026): Premium 35, Select 40, Mini 60.
  // Only the non-Select grades need an override; Select is the HYDRANGEA
  // category default above.
  "hydrangea-white-premium": { EB: 20, QB: 35, HB: null },
  "hydrangea-blue-premium": { EB: 20, QB: 35, HB: null },
  // Mini grade - a third tier the Jun 12 lock did not model.
  "hydrangea-green-mini": { EB: 20, QB: 60, HB: null },
  // NOTE: hydrangea-purple-premium is a MISLEADING SLUG - the variety is
  // grade Select and packs 40, NOT 35 (Alexis ruling, Jul 13, 2026; the
  // workbook's 35 was a slip). It deliberately has NO override so it picks up
  // the Select default. Do not "fix" this by adding one.
  //
  // Lilies are too bulky for Eighth Boxes and pack below the
  // specialty default.
  "asiatic-lily": { EB: null, QB: 70, HB: null },
  "oriental-lily": { EB: null, QB: 60, HB: null },
  // Corrected 80 -> 100 by Alexis ruling (Jun 12, 2026).
  sunflower: { EB: 30, QB: 100, HB: null },
  // leather-fern moved to FILLER_OVERRIDES (QB 400 -> 150, Jul 13, 2026).
};

// Effective sub-category for matrix lookup. Mirrors the seed builder
// in lib/catalog/static-varieties.ts applyDefaults().
export function packingCategory(v: {
  category: string;
  slug: string;
  compatibilityGroup?: string;
}): keyof typeof STEMS_PER_BUNCH {
  if (v.category === "rose") return "rose";
  if (v.category === "garden-spray-rose") {
    return v.compatibilityGroup === "B" ? "spray-rose" : "garden-rose";
  }
  if (v.category === "hydrangea") return "hydrangea";
  if (v.category === "specialty") {
    if (v.slug === "select-carnation") return "select-carnation";
    if (v.slug === "spray-carnation") return "spray-carnation";
    return "specialty";
  }
  return "filler-green";
}

// Expected BoxSizes dictionary for a variety per the locked matrix.
export function expectedBoxSizes(v: Variety): BoxSizes {
  const override = PER_VARIETY_OVERRIDES[v.slug];
  if (override) return { any: override };
  // Fillers/greens are per-variety since Jul 13, 2026 - there is no category
  // default left to fall back on.
  const filler = FILLER_OVERRIDES[v.slug];
  if (filler) return { any: filler };
  const cat = packingCategory(v);
  switch (cat) {
    case "rose": {
      const out: BoxSizes = {};
      for (const l of v.lengths) {
        out[String(l)] = l >= 70 ? ROSE_70 : ROSE_50_60;
      }
      return out;
    }
    case "garden-rose": {
      const out: BoxSizes = {};
      for (const l of v.lengths) out[String(l)] = GARDEN_ROSE;
      return out;
    }
    case "spray-rose": {
      const out: BoxSizes = {};
      for (const l of v.lengths) out[String(l)] = SPRAY_ROSE;
      return out;
    }
    case "hydrangea":
      return { any: HYDRANGEA };
    case "select-carnation":
    case "spray-carnation":
      return { any: CARNATION };
    case "specialty":
      return { any: SPECIALTY_DEFAULT };
    default:
      // A filler/green that is NOT in FILLER_OVERRIDES has no locked capacity.
      // Returning the retired all-null row makes the packing-matrix test fail
      // loudly on it, rather than silently blessing the dead EB100/QB300
      // default. Add the variety to FILLER_OVERRIDES.
      return { any: FILLER_GREEN_RETIRED };
  }
}

// Per-variety stems-per-bunch, where the variety does NOT follow its category.
// Alexis confirmed Jul 13, 2026: sunflowers pack 5 to a bunch, not the
// specialty default of 10. This was PRICING-RELEVANT - price per bunch is
// pricePerStem x stemsPerBunch, so at 10 the portal was quoting DOUBLE the
// real per-bunch price ($9.60 instead of $4.80). Stems per box are unchanged
// (EB 30 / QB 100), so box prices are unaffected; bunches per box double
// (EB 6, QB 20).
export const PER_VARIETY_STEMS_PER_BUNCH: Record<string, number> = {
  sunflower: 5,
};

export function expectedStemsPerBunch(v: Variety): number | null {
  const override = PER_VARIETY_STEMS_PER_BUNCH[v.slug];
  if (override !== undefined) return override;
  return STEMS_PER_BUNCH[packingCategory(v)] ?? null;
}

// ===========================================================================
// #468 pre-seed helpers: saleMode / mixGroup / unitsPerBox derived from the
// locked matrix above. These SEED the three editable /staff/availability fields
// so Alexis only adjusts. They are also the graceful fallback the availability
// tool uses for a DDB row that has not been synced yet. DATA ONLY - no box-fill
// / mix / checkout code reads these in this paste.
// ===========================================================================

// One mix group per sub-category (over-separated by design; Alexis merges the
// groups he wants to allow mixing, in the tool). The display sections stay on
// category, NOT mixGroup.
const SUBCATEGORY_MIX_GROUP: Record<SubCategory, number> = {
  "A-rose": 1, // roses
  "A-gardenrose": 2, // garden roses
  B: 3, // spray roses
  C: 4, // hydrangea
  "D-lily": 5, // lilies
  D: 6, // specialties
  E: 7, // greens
};

// Hydrangea is bought by the stem or the whole box, never by the bunch, so it
// fills its boxes in STEMS. Everything else pre-seeds to box_bunch. Nothing
// pre-seeds to box_only - Alexis marks the box-only varieties in the tool.
export function expectedSaleMode(v: {
  category: string;
  slug: string;
}): SaleMode {
  return v.category === "hydrangea" ? "box_stem" : "box_bunch";
}

export function expectedMixGroup(v: Variety): number | null {
  if (expectedSaleMode(v) === "box_only") return null;
  return SUBCATEGORY_MIX_GROUP[subCategoryOf(v)];
}

function bunchesFromStems(
  stems: number | null,
  stemsPerBunch: number | null,
): number | null {
  if (stems === null || stemsPerBunch === null || stemsPerBunch <= 0) {
    return null;
  }
  return Math.floor(stems / stemsPerBunch);
}

function cloneUnits(u: UnitsPerBox): UnitsPerBox {
  const out: UnitsPerBox = {};
  for (const [key, cap] of Object.entries(u)) {
    out[key] = { EB: cap.EB, QB: cap.QB, HB: cap.HB };
  }
  return out;
}

// units-per-box in the saleMode's unit. box_stem -> the stem capacity itself.
// box_bunch -> bunches = floor(stems / stemsPerBunch) per box code + length.
// Every variety derives cleanly from the locked matrix (e.g. sunflower QB 100
// stems / 5-per-bunch = 20 bunches, EB 30 / 5 = 6); Alexis fine-tunes any real
// count in the /staff/availability tool.
export function expectedUnitsPerBox(v: Variety): UnitsPerBox {
  const sizes = expectedBoxSizes(v);
  if (expectedSaleMode(v) === "box_stem") {
    return cloneUnits(sizes);
  }

  const spb = expectedStemsPerBunch(v);
  const out: UnitsPerBox = {};
  for (const [key, cap] of Object.entries(sizes)) {
    out[key] = {
      EB: bunchesFromStems(cap.EB, spb),
      QB: bunchesFromStems(cap.QB, spb),
      HB: bunchesFromStems(cap.HB, spb),
    };
  }
  return out;
}

const LABEL_TO_KEY: Record<string, keyof BoxCapacity> = {
  eighth: "EB",
  quarter: "QB",
  half: "HB",
};

// Lint a description MDX body against the matrix. `variants` are all
// catalog varieties the description file covers (grade siblings share
// one file keyed by groupSlug). Every numeric stem count stated for a
// box size - in the At-a-glance table or anywhere in prose - must be a
// valid capacity for at least one covered variant.
export function lintPackingNumbers(
  body: string,
  variants: Variety[],
): string[] {
  const violations: string[] = [];

  const allowed: Record<keyof BoxCapacity, Set<number>> = {
    EB: new Set(),
    QB: new Set(),
    HB: new Set(),
  };
  for (const v of variants) {
    const sizes = expectedBoxSizes(v);
    for (const cap of Object.values(sizes)) {
      for (const key of ["EB", "QB", "HB"] as const) {
        const stems = cap[key];
        if (stems !== null && stems !== undefined) allowed[key].add(stems);
      }
    }
  }

  // "Eighth Box (80)", "Quarter Box (35-40 stems by grade)",
  // "Half Box (300 stems)" - wherever they appear.
  const found = [...body.matchAll(/(Eighth|Quarter|Half) Box\s*\(([^)]*)\)/gi)];
  for (const m of found) {
    const key = LABEL_TO_KEY[m[1].toLowerCase()];
    const numbers = [...m[2].matchAll(/\d+/g)].map((n) => parseInt(n[0], 10));
    if (numbers.length === 0) continue; // qualitative parenthetical
    if (allowed[key].size === 0) {
      violations.push(
        `${m[1]} Box stem count stated but no covered variety packs that size`,
      );
      continue;
    }
    for (const n of numbers) {
      if (!allowed[key].has(n)) {
        violations.push(
          `${m[1]} Box stated as ${n} stems; locked matrix allows {${[...allowed[key]].join(", ")}}`,
        );
      }
    }
  }

  // 12.8: match the Spanish label too. The lint was English-only, so the ES
  // pages were never checked for stems-per-bunch at all (and, before this,
  // were not linted at all).
  const bunchRow = body.match(
    /^\|\s*(?:Stems per bunch|Tallos por bunch|Tallos por ramo)\s*\|(.+)\|\s*$/m,
  );
  const expectedBunch = new Set(
    variants
      .map((v) => expectedStemsPerBunch(v))
      .filter((n): n is number => n !== null),
  );
  if (bunchRow) {
    const num = bunchRow[1].match(/\d+/);
    if (!num) {
      violations.push(`Stems per bunch row has no number: "${bunchRow[1].trim()}"`);
    } else if (expectedBunch.size === 0) {
      violations.push(
        `Stems per bunch row present but covered varieties are box-purchase (no bunch)`,
      );
    } else if (!expectedBunch.has(parseInt(num[0], 10))) {
      violations.push(
        `Stems per bunch states ${num[0]}; locked matrix says {${[...expectedBunch].join(", ")}}`,
      );
    }
  } else if (expectedBunch.size > 0) {
    violations.push("missing Stems per bunch row in At a glance");
  }

  return violations;
}
